import React, { useEffect, useState, useCallback } from 'react';
import { gradeApi } from '../../../api.js';
import { useAuth } from '../../../auth/AuthContext.js';
import AssMeanMedian from './AssMeanMedian.js';
import SubQuestionTable from './SubQuestionTable.js';
import TScoreFrequency from './TScoreFrequency.js';
import { Loader2, AlertCircle, Sparkles } from 'lucide-react';
import { toast } from 'react-hot-toast';

const TAssignmentStats = ({ class_id, assignment_id }) => {
  const [scores, setScores] = useState([]);
  const [stdDeviation, setStdDeviation] = useState(0.0);
  const [assignment, setAssignment] = useState();
  const [mean, setMean] = useState(0.0);
  const [min, setMin] = useState(0);
  const [max, setMax] = useState(0);
  const [maxP, setMaxP] = useState(0);
  const [median, setMedian] = useState(0);
  const [submissions, setSubmissions] = useState([]);
  const { user } = useAuth();
  const [errorMessage, setErrorMessage] = useState('');
  const [isErrorVisible, setIsErrorVisible] = useState(false);
  const [isFeedbackPending, setIsFeedbackPending] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isGeneratingAnalysis, setIsGeneratingAnalysis] = useState(false);
  const [analysisGenerated, setAnalysisGenerated] = useState(false);
  const [aiOverallAnalysis, setAiOverallAnalysis] = useState(null);

  const getScores = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await gradeApi.get({
        path: `/api/assignment/${assignment_id}/submissions-stats/`,
        config: {
          headers: {
            Authorization: `Bearer ${user.session_token}`
          }
        }
      });

      const newScores = response.data.submissions.map(submission => submission.grade);
      const maxPossibleScore = response.data.assignment.questions.reduce((total, question) => {
        return (
          total +
          question.subQuestions.reduce((subTotal, subQuestion) => {
            // Use max_weight directly instead of calculating from rubric items
            return subTotal + (subQuestion.max_weight || 0);
          }, 0)
        );
      }, 0);

      // Check if grades are published but feedback is missing or empty
      const hasFeedback = response.data.assignment.questions.some(question => {
        return question.subQuestions.some(subQ => subQ.feedback && subQ.feedback.trim().length > 0);
      });

      setIsFeedbackPending(response.data.assignment.is_grades_published && !hasFeedback);
      setMaxP(maxPossibleScore);
      setAssignment(response.data.assignment);
      setSubmissions(response.data.submissions);
      setScores(newScores);
      get_min_max_median(newScores);
      if (response.data.assignment.ai_analysis) {
        setAiOverallAnalysis(response.data.assignment.ai_analysis);
      }
    } catch (error) {
      setErrorMessage('Failed to fetch scores. Please try again.');
      setIsErrorVisible(true);
    } finally {
      setIsLoading(false);
    }
  }, [assignment_id, user.session_token]);

  const get_min_max_median = scores => {
    const sortedArr = [...scores].sort((a, b) => a - b);
    setMin(sortedArr[0]);
    setMax(sortedArr[sortedArr.length - 1]);
    const median_to_set =
      sortedArr.length % 2 === 0
        ? (sortedArr[sortedArr.length / 2 - 1] + sortedArr[sortedArr.length / 2]) / 2
        : sortedArr[Math.floor(sortedArr.length / 2)];
    setMedian(median_to_set);
    const total = scores.reduce((acc, num) => acc + num, 0);
    setMean(total / scores.length);
  };

  useEffect(() => {
    getScores();
  }, [getScores]);

  // Event listener for AI analysis completion
  useEffect(() => {
    const handleAnalysisCompleted = event => {
      const { assignmentId } = event.detail;

      // Only refetch if the analysis for this assignment is completed
      if (assignmentId == assignment_id) {
        toast.success('AI Analysis is now available!');
        getScores(); // Refetch the data to get the updated analysis
      }
    };

    // Add event listener
    window.addEventListener('analysisCompleted', handleAnalysisCompleted);

    // Clean up the event listener on component unmount
    return () => {
      window.removeEventListener('analysisCompleted', handleAnalysisCompleted);
    };
  }, [assignment_id, getScores]);

  // Handle generating AI analysis
  const generateAIAnalysis = async () => {
    setIsGeneratingAnalysis(true);
    setAnalysisGenerated(true);

    try {
      await gradeApi.post({
        path: `/api/assignment/${assignment_id}/store-agg-feedback/`,
        body: { is_grades_published: true },
        config: {
          headers: {
            Authorization: `Bearer ${user.session_token}`,
            'Content-Type': 'application/json'
          }
        }
      });
      // Note: We don't need to handle polling here as TopBar.js now does this
    } catch (error) {
      toast.error('Failed to start AI analysis generation');
      setAnalysisGenerated(false);
    } finally {
      setIsGeneratingAnalysis(false);
    }
  };

  useEffect(() => {
    if (scores.length > 0) {
      const variance =
        scores.reduce((acc, num) => acc + Math.pow(num - mean, 2), 0) / scores.length;
      setStdDeviation(Math.sqrt(variance));
    }
  }, [scores, mean]);

  return (
    <div
      className="mt-5 p-6 rounded-lg shadow-sm transition-all duration-300"
      style={{
        background: 'linear-gradient(135deg, #ffffff, #fdf8f3)',
        border: '1px solid rgba(228, 177, 121, 0.2)',
        boxShadow:
          'rgba(228, 177, 122, 0.06) 0px 2px 4px -2px, rgba(228, 177, 122, 0.04) 0px 4px 8px -2px'
      }}
    >
      {isErrorVisible && (
        <div
          className="mb-6 p-4 rounded-lg flex items-center"
          style={{
            background: 'rgba(239, 68, 68, 0.05)',
            border: '1px solid rgba(239, 68, 68, 0.2)',
            borderLeft: '4px solid rgb(239, 68, 68)'
          }}
        >
          <AlertCircle size={18} className="mr-2" style={{ color: 'rgb(239, 68, 68)' }} />
          <span style={{ color: 'rgb(185, 28, 28)' }}>{errorMessage}</span>
        </div>
      )}

      {isLoading ? (
        <div className="flex justify-center items-center py-16">
          <Loader2 className="w-8 h-8 animate-spin" style={{ color: '#e4b17a' }} />
        </div>
      ) : (
        <>
          <AssMeanMedian
            mean={mean}
            stdDeviation={stdDeviation}
            min={min}
            max={max}
            median={median}
            title={assignment?.title + ' statistics'}
            max_score={maxP}
          />

          <div className="mt-6">
            <TScoreFrequency scores={scores} max={maxP} />
          </div>

          <div className="mt-6 flex flex-col items-center">
            {analysisGenerated && (
              <div
                className="px-6 py-4 rounded-lg mb-6 text-center max-w-2xl"
                style={{
                  background: 'rgba(228, 177, 121, 0.08)',
                  border: '1px solid rgba(228, 177, 121, 0.2)',
                  color: '#935e20'
                }}
              >
                <p>
                  AI Analysis generation has started! Please wait a few minutes to see the results.
                </p>
                <p>Or refresh the page if it doesn't appear automatically.</p>
              </div>
            )}
          </div>

          {aiOverallAnalysis && (
            <div
              className="mt-8 p-6 rounded-lg shadow-md w-full max-w-3xl mx-auto"
              style={{
                background: 'linear-gradient(135deg, #fdf6ec, #fefbf8)',
                border: '1px solid rgba(228, 177, 121, 0.3)'
              }}
            >
              <h3 className="text-xl font-semibold mb-4 text-center" style={{ color: '#8c5312' }}>
                <Sparkles size={20} className="inline mr-2 mb-1" />
                Assignment AI Analysis
              </h3>
              <div
                dangerouslySetInnerHTML={{
                  __html: aiOverallAnalysis.replace(/<FB>|<\/FB>/g, '').replace(/\n/g, '<br />')
                }}
                style={{ color: '#935e20' }}
              />
            </div>
          )}

          <div className="mt-6">
            <SubQuestionTable
              submissions={submissions}
              assignment_id={assignment_id}
              questions={assignment?.questions || []}
            />
          </div>

          {isFeedbackPending && (
            <div
              className="fixed bottom-6 right-6 bg-[rgba(17,24,39,0.95)] text-white py-3 px-6 rounded-2xl flex items-center gap-4 shadow-lg z-50"
              style={{
                backdropFilter: 'blur(10px)',
                WebkitBackdropFilter: 'blur(10px)',
                border: '1px solid rgba(255, 255, 255, 0.1)',
                boxShadow:
                  '0 4px 24px -6px rgba(0, 0, 0, 0.3), 0 12px 40px -8px rgba(0, 0, 0, 0.25), inset 0 1px 1px rgba(255, 255, 255, 0.1), inset 0 -1px 1px rgba(0, 0, 0, 0.1)',
                animation:
                  'slideUp 0.4s cubic-bezier(0.16, 1, 0.3, 1), glow 3s ease-in-out infinite'
              }}
            >
              <div
                className="flex-shrink-0 w-2.5 h-2.5 rounded-full bg-[#e4b17a]"
                style={{
                  boxShadow: '0 0 0 2px rgba(228, 177, 122, 0.2), 0 0 8px rgba(228, 177, 122, 0.4)',
                  animation: 'pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite'
                }}
              ></div>
              <div>
                <strong style={{ color: '#e4b17a', fontWeight: 600 }}>AI Analysis</strong> is being
                generated. Results will appear soon.
              </div>
            </div>
          )}
        </>
      )}

      <style jsx>{`
        @keyframes slideUp {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        @keyframes glow {
          0%,
          100% {
            box-shadow:
              0 4px 24px -6px rgba(0, 0, 0, 0.3),
              0 12px 40px -8px rgba(0, 0, 0, 0.25),
              inset 0 1px 1px rgba(255, 255, 255, 0.1),
              inset 0 -1px 1px rgba(0, 0, 0, 0.1);
          }
          50% {
            box-shadow:
              0 4px 24px -6px rgba(0, 0, 0, 0.3),
              0 12px 40px -8px rgba(0, 0, 0, 0.25),
              0 0 20px rgba(228, 177, 122, 0.2),
              inset 0 1px 1px rgba(255, 255, 255, 0.1),
              inset 0 -1px 1px rgba(0, 0, 0, 0.1);
          }
        }

        @keyframes pulse {
          0%,
          100% {
            transform: scale(1);
            opacity: 1;
          }
          50% {
            transform: scale(1.2);
            opacity: 0.8;
            filter: brightness(1.2);
          }
        }
      `}</style>
    </div>
  );
};

export default TAssignmentStats;
